import { ComponentProps, useCallback, useContext } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { SplitListingToOriginalDialog } from 'src/dialogs/SplitListingToOriginalDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ModalProps } from 'src/modals/Modal';
import { SplitListingModal } from 'src/modals/SplitListing';
import { SplitIcon } from 'src/svgs/SplitIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Feature,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  SplitListingInput,
} from 'src/WebApiController';

export const LaunchSplitListingToOriginal = ({
  listingId,
  cancelTo,
  variant,
  disabled,
  iconOnlyMode,
  modalSize,
  ...buttonProps
}: {
  listingId: number;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
  modalSize?: ModalProps['size'];
} & ComponentProps<typeof SplitListingModal>) => {
  const { closeModal } = useContext(ModalContext);
  const { posEntity: listing, setActivePosEntity } =
    useActivePosEntityContext<ListingDetails>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const hasConsigneeSingleEntityUpdateFeature = useUserHasFeature(
    Feature.ConsigneeSingleEntityUpdate
  );

  const { openDialog, closeDialog } = useDialog(
    DialogId.Confirm,
    SplitListingToOriginalDialog
  );

  const { showErrorDialog } = useClientLogContext();

  const splitListing = useCallback(
    async (splitListingInput: SplitListingInput) => {
      return await tryInvokeApi(
        async () => {
          const result = hasConsigneeSingleEntityUpdateFeature
            ? await new ListingClient(
                activeAccountWebClientConfig
              ).splitListingsByListingId(
                splitListingInput.sourceListingId,
                splitListingInput!
              )
            : await new ListingClient(
                activeAccountWebClientConfig
              ).splitListings(splitListingInput!);

          if (result) {
            refreshExpandedListItems();

            // After we split - the original listing is gone - so we don't want to cancel back to it
            // so we just close the main modal
            closeModal(true);
          }
        },
        (error) => {
          showErrorDialog('ListingClient.splitListings', error);
        },
        () => {
          closeDialog();
        }
      );
    },
    [
      activeAccountWebClientConfig,
      closeDialog,
      closeModal,
      hasConsigneeSingleEntityUpdateFeature,
      refreshExpandedListItems,
      showErrorDialog,
    ]
  );

  const onLaunchDialog = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!listing?.tickets?.length) {
        // If we haven't pulled tickets, pull it
        setActivePosEntity(listingId, null, false, [
          ListingDetailDataField.Basic,
          ListingDetailDataField.Tickets,
        ]);
      }

      openDialog({
        onOkay: async (splitListingInput) => {
          await splitListing(splitListingInput);
          closeDialog();
        },

        onCancel: () => {
          closeDialog();
        },
        fullscreen: modalSize === 'fullscreen',
      });
    },
    [
      listing?.tickets?.length,
      openDialog,
      modalSize,
      setActivePosEntity,
      listingId,
      splitListing,
      closeDialog,
    ]
  );

  return (
    <ButtonWithIcon
      {...buttonProps}
      variant={variant}
      textColor={variant === 'textPlain' ? 'strong' : 'brand'}
      style={variant === 'textPlain' ? { width: '100%' } : undefined}
      justifyContent="space-between"
      iconOnlyMode={iconOnlyMode}
      onClick={onLaunchDialog}
      textContentId={ContentId.ResetToOriginal}
      disabled={disabled}
      icon={
        <SplitIcon
          withHoverEffect
          stroke={
            iconOnlyMode || (variant && variant !== 'regular')
              ? vars.color.textBrand
              : vars.color.textInverted
          }
          size={vars.iconSize.m}
        />
      }
    />
  );
};
